import {
	Rfx,
	RfxClientDocument,
	RfxKeys,
	rfxKeys,
	RfxType,
} from "newui/features/Rfx/types/rfx.types";
import { RfxTabNumbers } from "../types/component-types";
import { RfxQuestionType } from "../Submission";
import { QUESTION_TYPE_ID } from "../Submission/constants";
import {
	CreateQuote,
	CreateReturnableSchedule,
	CreateRFx,
	CreateRfxSubmissionFormat,
	CreateRFxTemplate,
} from "../types/Creation/create-rfx-form-types";
import dayjs from "newui/utils/dayjs";
import { isNullOrUndefined } from "newui/utils/objectUtil";
import { Company } from "newui/features/Company/types";
import {
	RfxQuestion,
	RfxQuestionSection,
	RfxReturnableSchedule,
} from "../types/returnable-schedule-question.type";
import { safeRandomUUID } from "newui/utils/general";
import { timezoneChecker } from "utils/helpers";

/**
 * Ensures that the saved data is merged with any unsaved data on the client
 * @param newData the new data from teh server, transformed into formsData
 * @param selectedTab the tab currently selected - used ot determine which data is source of truth
 * @param currentData the current forms data
 * @returns the correct formsdata
 */
export const mergeCreateRFxFormData = (
	newData: any,
	selectedTab: RfxTabNumbers,
	currentData: any
): Rfx => {
	switch (selectedTab) {
		// we have succesfully saved quotes
		// ensure we use local sections in case they are unsaved
		case RfxTabNumbers.Quote: {
			newData.sections = currentData.sections;
			break;
		}
		case RfxTabNumbers.ReturnableSchedule: {
			// we have succesfully saved returnable schedules
			// ensure we use local quotes in case they are unsaved
			newData.quote = currentData.quote;
			break;
		}
		default: {
			// ensure we use local quotes + returnable schedules in case they are unsaved
			newData.sections = currentData.sections;
			newData.quote = currentData.quote;
			break;
		}
	}

	return newData;
};

export const getDefaultValueForQuestionType = (
	questionType: RfxQuestionType
) => {
	switch (questionType) {
		case QUESTION_TYPE_ID.MULTIPLE_CHOICE:
		case QUESTION_TYPE_ID.SINGLE_OPTION:
		case QUESTION_TYPE_ID.DROP_DOWN:
			return []; // array of options

		case QUESTION_TYPE_ID.TABLE:
		case QUESTION_TYPE_ID.TABLE_ROWS:
			return []; // array of rows/columns

		case QUESTION_TYPE_ID.CORPORATE_DOCUMENTS:
			return { allow_document_categories: [] }; // structured object

		case QUESTION_TYPE_ID.FILE_UPLOAD:
			return []; // list of files

		default:
			return null;
	}
};

export const processFormValuesForSaving = (
	additional_message: string | null,
	template: CreateRFxTemplate,
	values: CreateRFx
): CreateRfxSubmissionFormat => {
	const isTemplate = !isNullOrUndefined(template);

	const baseSubmissionFormat = {
		...values,
		additional_message,
		location: values?.address?.value || "",
		location_latitude: values?.address?.latitude || "",
		location_longitude: values?.address?.longitude || "",
		state: values?.state || "",
		// Interpret the picked date + time as wall-clock in the selected
		// submission timezone, then store as a UTC instant so it round-trips
		// consistently with the read-only view and backend deadline checks.
		submission_deadline: values?.deadline_date
			? dayjs
					.tz(
						`${dayjs(values?.deadline_date).format("YYYY-MM-DD")} ${values?.deadline_time}`,
						"YYYY-MM-DD HH:mm",
						timezoneChecker(values?.submission_deadline_timezone)
					)
					.utc()
					.format("YYYY-MM-DD HH:mm:ss")
			: "",
		// Explicitly remove fields managed by conditional logic below
		quote: undefined,
		quote_file: undefined,
		quote_file_path: undefined,
		sections: undefined,
		returnable_schedule_file: undefined,
		returnable_schedule_file_path: undefined,
		returnable_schedule_file_id: undefined,
		...(isTemplate && {
			...template,
		}),
	} as CreateRFx;

	// Conditionally add quote-related fields
	let quoteSubmission = {};
	if (values?.quote_type === "file") {
		quoteSubmission = {
			quote: [], // always empty for file type
			...(isTemplate
				? {
						quote_file_id: values.quote_file_id || null,
						quote_file: null,
						quote_file_path: "",
					}
				: {
						quote_file_id: null,
						quote_file: values.quote_file,
						quote_file_path: values.quote_file_path,
					}),
		} as CreateQuote;
	} else {
		quoteSubmission = {
			quote: values?.quote || [],
			quote_file: "",
			quote_file_path: "",
			quote_file_id: null,
		} as CreateQuote;
	}

	let scheduleSubmission = {};

	if (values?.returnable_schedule_type === "file") {
		scheduleSubmission = {
			sections: [], // always empty for file type
			returnable_schedule_file_id: isTemplate
				? values.returnable_schedule_file_id || null
				: null,
			returnable_schedule_file: isTemplate
				? null
				: values.returnable_schedule_file,
			returnable_schedule_file_path: isTemplate
				? ""
				: values.returnable_schedule_file_path,
		} as CreateReturnableSchedule;
	} else {
		scheduleSubmission = {
			sections:
				values?.sections?.map((section) => {
					const sectionId = isNaN(section.id) ? null : section.id;
					const questionnaire = section.questionnaire?.map((question) => {
						const questionId = isNaN(question.id) ? null : question.id;
						return { ...question, id: questionId } as RfxQuestion;
					});
					return {
						...section,
						id: sectionId,
						questionnaire: questionnaire,
					} as RfxQuestionSection;
				}) || [],
			returnable_schedule_file: "",
			returnable_schedule_file_path: "",
			returnable_schedule_file_id: null,
		} as CreateReturnableSchedule;
	}

	return {
		...baseSubmissionFormat,
		...quoteSubmission,
		...scheduleSubmission,
		quote_type: values.quote_type,
		returnable_schedule_type: values.returnable_schedule_type,
	} as CreateRfxSubmissionFormat;
};

const processReturnableScheduleForTemplatePreview = (
	returnableSchedule: RfxReturnableSchedule
): RfxReturnableSchedule => {
	return returnableSchedule.map((section, index) => {
		const questionnaire = section.questionnaire
			? section.questionnaire.map((question) => {
					return {
						...question,
						id: question.id ?? safeRandomUUID(),
					} as RfxQuestion;
				})
			: [];
		return {
			...section,
			id: section.id ?? Date.now() + index,
			questionnaire: questionnaire,
		} as RfxQuestionSection;
	});
};

const handleSpecialMappingCase = (
	field: RfxKeys,
	rfxFormData: CreateRFx,
	rfxId: number,
	isTemplate: boolean
): any => {
	switch (field) {
		case "allow_flexible_quote":
			return rfxFormData.allow_flexible_quote === "1" ? true : false;
		case "documentationDocument":
			return rfxFormData.documents
				? rfxFormData.documents.map((document) => {
						return {
							categoryId: document.category_id,
							documentPath: document.path,
							fileName: document.document,
							fileSize: document.file_size,
							id: document.id,
							rfqId: rfxId,
						} as RfxClientDocument;
					})
				: null;
		case "questionnaire":
			return isTemplate
				? processReturnableScheduleForTemplatePreview(rfxFormData.sections)
				: rfxFormData.sections;
		case "quoteFile":
			if (!rfxFormData.quote_file_path) {
				return null;
			}
			return {
				file_name: rfxFormData.quote_file,
				file_path: rfxFormData.quote_file_path,
			};
		case "quotes":
			return (rfxFormData.quote || []).map((q: any) => ({
				...q,
				rows: q.rows || q.items || [],
			}));
		case "returnableScheduleFile":
			if (isTemplate) {
				return {
					file_name: rfxFormData.returnable_schedule_file,
					file_path: rfxFormData.returnable_schedule_file_path,
				};
			}

			return rfxFormData.returnable_schedule_file;
		case "submissionDeadline": {
			// Combine the form's date + time (wall-clock in the selected timezone)
			// into a UTC instant, matching how the deadline is serialised on save.
			// deadline_date is a date-only "YYYY-MM-DD" string, so it must be
			// paired with deadline_time or the preview shows midnight (e.g. 8am
			// AWST) instead of the chosen time.
			if (!rfxFormData.deadline_date || !rfxFormData.deadline_time) {
				return null;
			}
			return dayjs
				.tz(
					`${dayjs(rfxFormData.deadline_date).format("YYYY-MM-DD")} ${rfxFormData.deadline_time}`,
					"YYYY-MM-DD HH:mm",
					timezoneChecker(rfxFormData.submission_deadline_timezone)
				)
				.toISOString();
		}
	}
};

export const convertDraftRfxDataToRfx = (
	rfxFormData: CreateRFx,
	existingRfxData: Rfx,
	rfxTypes: RfxType[],
	currentCompany: Company,
	isTemplate = false
): Rfx => {
	const getValue = (obj: any, path: string): any =>
		path.split(".").reduce((acc, key) => acc?.[key], obj);

	// Field mapping overrides for CreateRFx ( we get these from Formik Formvalues)
	const createRfxFieldMap: Record<string, string> = {
		escopeOfService: "escope_of_service",
		documentationDescription: "documentation_description",
		location: "address.value",
		locationLatitude: "address.latitude",
		locationLongitude: "address.longitude",
		questionnaire: "sections",
		quoteType: "quote_type",
		returnableScheduleFile: "returnable_schedule_file",
		returnableScheduleType: "returnable_schedule_type",
		rfqIdNumber: "rfq_id_number",
		submissionDeadline: "deadline_date", // combined with time + timezone later
		submissionTimezone: "submission_deadline_timezone",
	};

	// cycle thorugh all Rfx Fields and assign values
	const newData = rfxKeys.reduce((acc, field) => {
		// check if we have any mapped values
		const mappedKey = createRfxFieldMap[field as RfxKeys] ?? field;

		const value =
			handleSpecialMappingCase(
				field,
				rfxFormData,
				existingRfxData.id,
				isTemplate
			) ?? getValue(rfxFormData, mappedKey);

		(acc as any)[field as RfxKeys] = value ?? existingRfxData[field];

		return acc;
	}, {} as Rfx);

	// handle RfxType special case
	if (rfxFormData.type) {
		newData["rfxType"] = rfxTypes?.find((t) => t.code === rfxFormData.type);
	}

	// hanlde company owner
	newData["companyOwner"] = currentCompany;

	return newData;
};
